import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import analytics from './analytics.js';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [allData, setAllData] = useState(null);
  const [view, setView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Simple authentication check - require specific session key
    const adminKey = sessionStorage.getItem('admin_access');
    if (adminKey === 'seabird_admin_2024') {
      setAuthenticated(true);
      loadData();
      const interval = setInterval(loadData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    } else {
      // Prompt for admin access
      const key = prompt('Admin access required. Enter admin key:');
      if (key === 'seabird2024admin') {
        sessionStorage.setItem('admin_access', 'seabird_admin_2024');
        setAuthenticated(true);
        loadData();
        const interval = setInterval(loadData, 30000);
        return () => clearInterval(interval);
      } else {
        alert('Access denied. Redirecting to home page.');
        navigate('/');
      }
    }
  }, [navigate]);

  const loadData = async () => {
    try {
      const [statsData, allAnalyticsData] = await Promise.all([
        analytics.getStats(),
        analytics.getAllData()
      ]);
      
      setStats(statsData);
      setAllData(allAnalyticsData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load analytics data:', error);
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  const clearAllData = async () => {
    if (window.confirm('Are you sure you want to clear ALL analytics data? This cannot be undone.')) {
      try {
        await analytics.clearData();
        await loadData();
        alert('Analytics data cleared successfully!');
      } catch (error) {
        console.error('Failed to clear data:', error);
        alert('Failed to clear data. Please try again.');
      }
    }
  };

  if (!authenticated || loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>🔒 Admin Dashboard</h1>
        <p>{!authenticated ? 'Authenticating...' : 'Loading analytics data...'}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>🔒 Admin Dashboard</h1>
        <p>No analytics data available.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ textAlign: 'center', color: '#333', marginBottom: '30px' }}>
          🔒 Seabird Recipe Book - Admin Dashboard
        </h1>
        
        {/* Navigation */}
        <div style={{ marginBottom: '30px', textAlign: 'center' }}>
          {['overview', 'recipes', 'audio', 'devices', 'errors', 'raw'].map(tab => (
            <button
              key={tab}
              onClick={() => setView(tab)}
              style={{
                margin: '0 10px',
                padding: '10px 20px',
                backgroundColor: view === tab ? '#007bff' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {view === 'overview' && (
          <div>
            {/* Data Source Indicator */}
            <div style={{ marginBottom: '20px', textAlign: 'center', display: 'flex', gap: '15px', justifyContent: 'center', alignItems: 'center' }}>
              <span style={{ 
                padding: '5px 15px', 
                backgroundColor: allData?.source === 'api' ? '#28a745' : '#ffc107',
                color: 'white',
                borderRadius: '15px',
                fontSize: '14px'
              }}>
                📊 Data Source: {allData?.source === 'api' ? 'MySQL Database' : 'Local Storage'}
              </span>
              
              <button
                onClick={() => {
                  analytics.trackEvent('admin_test_event', { 
                    test: true, 
                    timestamp: Date.now() 
                  });
                  setTimeout(() => loadData(), 1000);
                }}
                style={{
                  padding: '5px 15px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '15px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                🧪 Test Analytics
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              {/* Summary Cards */}
              <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#28a745' }}>📊 Total Events</h3>
                <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                  {(Array.isArray(stats.totalEvents) ? stats.totalEvents[0]?.count : stats.totalEvents || 0).toLocaleString()}
                </p>
              </div>
              
              <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#007bff' }}>👥 Unique Sessions</h3>
                <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                  {Array.isArray(stats.uniqueSessions) ? stats.uniqueSessions[0]?.count : stats.uniqueSessions || 0}
                </p>
              </div>
              
              <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#ffc107' }}>📱 Data Range</h3>
                <p style={{ fontSize: '14px', margin: 0 }}>
                  {stats.dateRange ? 
                    formatDuration(stats.dateRange.newest - stats.dateRange.oldest) :
                    'N/A'
                  }
                </p>
              </div>
              
              <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#dc3545' }}>⚠️ Errors</h3>
                <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                  {(stats.errors?.length || allData?.errors?.length || 0)}
                </p>
              </div>
            </div>

            {/* Activity Timeframes */}
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '30px' }}>
              <h3 style={{ marginTop: 0 }}>🕒 Activity Timeframes</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                <div>
                  <strong>Last 24 Hours:</strong> {Array.isArray(stats.timeframes) ? stats.timeframes[0]?.last24h : stats.timeframes?.last24h || 0} events
                </div>
                <div>
                  <strong>Last 7 Days:</strong> {Array.isArray(stats.timeframes) ? stats.timeframes[0]?.last7d : stats.timeframes?.last7d || 0} events
                </div>
                <div>
                  <strong>Last 30 Days:</strong> {Array.isArray(stats.timeframes) ? stats.timeframes[0]?.last30d : stats.timeframes?.last30d || 0} events
                </div>
              </div>
            </div>

            {/* Event Types */}
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginTop: 0 }}>📋 Event Types</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {(Array.isArray(stats.eventTypes) ? 
                  stats.eventTypes.map(item => [item.event_type, item.count]) :
                  stats.eventTypes ? Object.entries(stats.eventTypes) : []
                )
                  .sort(([,a], [,b]) => (b || 0) - (a || 0))
                  .map(([type, count]) => (
                    <div key={type} style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                      <strong>{(type || '').replace(/_/g, ' ')}:</strong> {count || 0}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {view === 'recipes' && (
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>🍳 Recipe Analytics</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Recipe ID</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Views</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Audio Plays</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Engagement Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(allData?.recipes || 
                    (stats.recipes ? Object.values(stats.recipes) : [])
                   )
                    .sort((a, b) => (b.total_views || b.views || 0) - (a.total_views || a.views || 0))
                    .map(recipe => (
                      <tr key={recipe.recipe_id || recipe.id}>
                        <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                          {recipe.recipe_id || recipe.id}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                          {recipe.recipe_name || recipe.name || 'Unknown'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>
                          {recipe.total_views || recipe.views || 0}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>
                          {recipe.audio_plays || recipe.audioPlays || 0}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>
                          {recipe.engagement_rate || 
                           (recipe.views > 0 ? `${((recipe.audioPlays / recipe.views) * 100).toFixed(1)}%` : '0%') ||
                           '0%'
                          }
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'audio' && (
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>🎵 Audio Analytics</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              <div>
                <h4>Playback Speeds</h4>
                {(allData?.source === 'api' ? 
                  // Use separate API call for audio speeds when using API
                  (stats.audio ? Object.entries(stats.audio) : []) :
                  Object.entries(stats.audio || {})
                )
                  .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                  .map(([speed, count]) => (
                    <div key={speed} style={{ padding: '8px', backgroundColor: '#f8f9fa', margin: '5px 0', borderRadius: '4px' }}>
                      <strong>{speed}x speed:</strong> {count || 0} plays
                    </div>
                  ))}
              </div>
              
              {allData?.audio && (
                <div>
                  <h4>Top Audio Files</h4>
                  {allData.audio.slice(0, 10).map(audio => (
                    <div key={`${audio.recipe_id}-${audio.step_number}`} style={{ padding: '8px', backgroundColor: '#f8f9fa', margin: '5px 0', borderRadius: '4px' }}>
                      <strong>Recipe {audio.recipe_id} Step {audio.step_number}:</strong> {audio.total_plays} plays
                      <br />
                      <small>Completion rate: {audio.completion_rate}%</small>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'devices' && (
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>📱 Device & Browser Analytics</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
              <div>
                <h4>Device Combinations</h4>
                {(Array.isArray(stats.deviceStats) ? 
                  stats.deviceStats.map(device => [`${device.device_type}-${device.browser}-${device.os}`, device.count]) :
                  stats.devices ? Object.entries(stats.devices) : []
                )
                  .sort(([,a], [,b]) => (b || 0) - (a || 0))
                  .map(([device, count]) => (
                    <div key={device} style={{ padding: '8px', backgroundColor: '#f8f9fa', margin: '5px 0', borderRadius: '4px' }}>
                      <strong>{device}:</strong> {count || 0} sessions
                    </div>
                  ))}
              </div>
              <div>
                <h4>Browsers</h4>
                {(stats.browsers ? Object.entries(stats.browsers) : 
                  Array.isArray(stats.deviceStats) ? 
                    stats.deviceStats.reduce((acc, device) => {
                      acc[device.browser] = (acc[device.browser] || 0) + device.count;
                      return acc;
                    }, {}) : {}
                )
                  ? Object.entries(stats.browsers ? stats.browsers : 
                      Array.isArray(stats.deviceStats) ? 
                        stats.deviceStats.reduce((acc, device) => {
                          acc[device.browser] = (acc[device.browser] || 0) + device.count;
                          return acc;
                        }, {}) : {}
                    )
                    .sort(([,a], [,b]) => (b || 0) - (a || 0))
                    .map(([browser, count]) => (
                      <div key={browser} style={{ padding: '8px', backgroundColor: '#f8f9fa', margin: '5px 0', borderRadius: '4px' }}>
                        <strong>{browser}:</strong> {count || 0} sessions
                      </div>
                    ))
                  : <div>No browser data available</div>
                }
              </div>
            </div>
          </div>
        )}

        {view === 'errors' && (
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>⚠️ Error Log</h3>
            {((allData?.errors?.length || 0) === 0 && (stats.errors?.length || 0) === 0) ? (
              <p style={{ color: '#28a745' }}>✅ No errors recorded!</p>
            ) : (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {(allData?.errors || stats.errors || [])
                  .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
                  .map((error, index) => (
                    <div key={index} style={{ 
                      padding: '15px', 
                      margin: '10px 0', 
                      backgroundColor: '#f8d7da', 
                      border: '1px solid #f5c6cb', 
                      borderRadius: '4px' 
                    }}>
                      <div><strong>Time:</strong> {formatDate(error.timestamp)}</div>
                      <div><strong>Error:</strong> {error.error_message || error.data?.error || 'Unknown error'}</div>
                      {(error.context || error.data?.context) && (
                        <div><strong>Context:</strong> {JSON.stringify(error.context || error.data.context)}</div>
                      )}
                      {(error.error_stack || error.data?.stack) && (
                        <details style={{ marginTop: '10px' }}>
                          <summary>Stack Trace</summary>
                          <pre style={{ fontSize: '12px', overflow: 'auto' }}>{error.error_stack || error.data.stack}</pre>
                        </details>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {view === 'raw' && (
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>🗃️ Raw Data</h3>
            <div style={{ marginBottom: '20px' }}>
              <button 
                onClick={clearAllData}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                Clear All Data
              </button>
              <button 
                onClick={() => {
                  const dataStr = JSON.stringify(allData, null, 2);
                  const dataBlob = new Blob([dataStr], {type: 'application/json'});
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `analytics-${new Date().toISOString().split('T')[0]}.json`;
                  link.click();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Export Data
              </button>
            </div>
            <div style={{ maxHeight: '500px', overflowY: 'auto', backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px' }}>
              <pre style={{ fontSize: '12px', margin: 0 }}>
                {JSON.stringify(allData, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '40px', padding: '20px', backgroundColor: 'white', borderRadius: '8px' }}>
          <p style={{ margin: 0, color: '#6c757d' }}>
            Last updated: {formatDate(Date.now())} | 
            Session ID: {allData?.sessionId} | 
            Auto-refresh: 30s
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;